import dayjs from 'dayjs';
import _ from 'lodash';
import { useMemo } from 'react';
import { useTheme } from 'next-themes';
import {
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLegend,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';

const LIGHT_LINE_COLORS = ['rgb(4, 79, 137)', 'rgb(0,124,192)'];

const DARK_LINE_COLORS = ['rgb(0, 160, 224)', 'rgb(148, 217, 243)'];

export function MultiLineChart({ filteredEntries = null, mode, question }) {
  const entries = filteredEntries ?? question.patientEntries;

  const { theme } = useTheme();

  const LINE_COLORS = theme === 'light' ? LIGHT_LINE_COLORS : DARK_LINE_COLORS;

  const data = entries.map((entry) => {
    return {
      ...entry,
      // Victory requires the date to be a JS date object
      date: new Date(entry.date),
    };
  });

  const areaData = [...data];
  _.remove(areaData, ['skipped', true]);

  const scatterDotSize = (datum, safetyMin, safetyMax, i) => {
    if (datum.skipped) return 0;

    // if (_.filter(data, (datum) => !datum.skipped).length === 1) {
    //   return 3;
    // }

    const dangerZone =
      datum[`answer_${i}`] > safetyMax || datum[`answer_${i}`] < safetyMin;

    if (dangerZone) {
      return 4;
    }
    return 2;
  };

  const independentAxis = useMemo(() => {
    const defaultProps = {
      tickValues: null,
      tickFormat: null,
    };

    switch (mode) {
      case 'day': {
        const startOfDay = dayjs(data[0].date).startOf('day');
        const tickValues = [startOfDay];

        for (let i = 0; i <= 2; i += 1) {
          const hour = dayjs(tickValues[i]).add(6, 'hours');
          tickValues.push(new Date(hour));
        }

        return {
          tickValues,
          tickFormat: (datum, i) => {
            if (i === 0) return `${dayjs(datum).format('MMM D')}`;
            return `${dayjs(datum).format('ha')}`;
          },
        };
      }

      case 'week': {
        const startOfWeek = dayjs(data[0].date).startOf('isoWeek');
        const tickValues = [];

        for (let i = 0; i <= 6; i += 1) {
          const dayOfWeek = dayjs(startOfWeek).add(i, 'days');
          tickValues.push(new Date(dayOfWeek));
        }

        return {
          tickValues,
          tickFormat: (datum) => `${dayjs(datum).format('ddd')}`,
        };
      }

      case 'all':
      default:
        return defaultProps;
    }
  }, [data, mode]);

  const { tickFormat, tickValues } = independentAxis;
  const { yMin, yMax, stepValue, answers } = question;

  const legend = answers.map((answer, i) => {
    return {
      name: answer.chartLabel,
      symbol: { fill: LINE_COLORS[i] },
    };
  });
  const numRows = Math.ceil(legend.length / 2);

  const answerLabel = (datum) => {
    if (!_.isArray(datum.answer)) {
      return null;
    }

    let date = dayjs(datum.date).format('LL');

    if (mode === 'day') {
      date = dayjs(datum.date).format('ha');
    }

    return `${date}\n ${datum.answer.join('/')} ${question.unit}`;
  };

  const CirclePoint = (props) => {
    const { x, y, active } = props;

    if (active)
      return (
        <g>
          <line
            x1={x}
            y1={0}
            x2={x}
            y2={500}
            stroke="#777777"
            stroke-dasharray={3}
          />
          <circle cx={x} cy={y} r="3" fill="#ffffff" />
        </g>
      );

    return;
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: `auto 1fr`,
        position: 'relative',
        marginTop: 0,
      }}
    >
      <div style={{ zIndex: 2 }}>
        <VictoryLegend
          x={12}
          y={0}
          orientation="horizontal"
          gutter={40}
          itemsPerRow={2}
          style={{
            labels: {
              fontSize: 14,
              fill: 'grey',
            },
          }}
          titleComponent={<VictoryLabel />}
          data={legend}
          height={30 * numRows}
        />
      </div>
      {/* -30 margin for the legend to overlay on the whitespace of the graph*/}
      <div style={{ marginTop: -30, zIndex: 1 }}>
        <VictoryChart
          data={data}
          x="date"
          scale={{ x: 'time' }}
          minDomain={{ y: yMin }}
          maxDomain={{ y: yMax }}
          height={400}
          width={600}
          containerComponent={
            <VictoryVoronoiContainer
              role="presentation"
              voronoiBlacklist={['area', 'scatter']}
              voronoiDimension="x"
              labels={({ datum }) =>
                datum.skipped ? null : answerLabel(datum)
              }
              labelComponent={
                <VictoryTooltip
                  y0={-100}
                  flyoutWidth={120}
                  flyoutHeight={60}
                  pointerWidth={18}
                  cornerRadius={0}
                  constrainToVisibleArea
                  flyoutStyle={{
                    fill: theme === 'light' ? 'black' : 'white',
                  }}
                  style={{
                    fill: theme === 'light' ? 'white' : 'black',
                  }}
                />
              }
            />
          }
        >
          {!_.isEmpty(tickValues) ? (
            <VictoryAxis
              fixLabelOverlap
              tickValues={tickValues}
              tickFormat={tickFormat}
              style={{
                axis: { stroke: 'grey' },
                tickLabels: { fill: 'grey', fontSize: 18 },
              }}
            />
          ) : (
            <VictoryAxis
              fixLabelOverlap
              tickFormat={tickFormat}
              style={{
                axis: { stroke: 'grey' },
                tickLabels: { fill: 'grey' },
              }}
            />
          )}
          <VictoryAxis
            dependentAxis
            invertAxis={question.reverseY}
            tickValues={_.range(yMin, yMax, stepValue).concat(yMax)}
            tickFormat={_.range(yMin, yMax, stepValue).concat(yMax)}
            style={{
              axis: { stroke: 'grey' },
              grid: { stroke: 'grey' },
              tickLabels: { fill: 'grey', fontSize: 18 },
            }}
          />
          {question.answers.map((_answer, i) => {
            const color = LINE_COLORS[i];

            return (
              <VictoryLine
                key={i}
                scale="time"
                style={{
                  parent: { border: '1px solid #ccc' },
                  data: {
                    stroke: color,
                    strokeWidth: 2,
                  },
                }}
                x="date"
                y={`answer_${i}`}
                data={areaData}
                name="area"
                y0={() => (question.reverseY ? yMax : yMin)}
              />
            );
          })}
          {question.answers.map((answer, i) => {
            return (
              <VictoryScatter
                name={i === 0 ? null : 'scatter'}
                key={i}
                data={data}
                x="date"
                dataComponent={<CirclePoint />}
                y={`answer_${i}`}
              />
            );
          })}
          {question.answers.map((answer, i) => {
            const color = LINE_COLORS[i];
            const { safetyMin, safetyMax } = answer;

            return (
              <VictoryScatter
                key={i}
                name="scatter"
                data={data}
                x="date"
                y={`answer_${i}`}
                size={({ datum }) =>
                  scatterDotSize(datum, safetyMin, safetyMax, i)
                }
                style={{
                  data: {
                    fill: ({ datum }) =>
                      datum[`answer_${i}`] > safetyMax ||
                      datum[`answer_${i}`] < safetyMin
                        ? 'red'
                        : color,
                    stroke: ({ datum }) =>
                      datum[`answer_${i}`] > safetyMax ||
                      datum[`answer_${i}`] < safetyMin
                        ? 'red'
                        : color,
                    strokeWidth: 2,
                  },
                }}
              />
            );
          })}
        </VictoryChart>
      </div>
    </div>
  );
}
